import React, {
  ComponentType,
  useCallback,
  useEffect,
  useState
} from 'react'
import { connect } from 'react-redux'
import {
  Accordion,
  Button,
  Container,
  Grid,
  Header,
  Icon,
  Modal,
  Segment
} from 'semantic-ui-react'
import { AxiosResponse } from 'axios'
import {
  RouteComponentProps,
  withRouter
} from 'react-router-dom'
// Local
import {
  allowEncounterCDICopyPaste,
  allowPatientTabSchedulingCdi,
  encounterConditionCdiDisplayVersion
} from '../../common/currentUserUtils'
import {
  EncounterConditionDisplay,
  EncounterConditionRuleRow,
  EncounterDetail
} from '../../authenticated/types'
import {
  API_URL,
  CONDITION,
  ENCOUNTER,
  HCC_GROUP,
  LOCK,
  NOTE,
  PAGES,
  REVIEW,
  shouldRenderReskin2025
} from '../../../constant'
import { api } from '../../../action/api'
import * as lockUtils from '../../common/lockUtils'
import {
  spliceCondition,
  spliceConditions,
  withEncounter
} from '../../common/conditionUtils'
import ConditionHeader from '../common/conditionHeader'
import EncounterNotes from '../common/EncounterNotes'
import PreviousEncounters from '../common/previousEncounters'
import PreviousEncountersSticky from '../common/previousEncountersSticky'
import CdiEncounterHeader from './cdiEncounterHeader'
import CdiEncounterLeft from './cdiEncounterLeft'
import CdiEncounterConditionsV1 from './cdiEncounterConditionsV1'
import CdiEncounterConditionsV2 from './cdiEncounterConditionsV2'
import CdiEncounterMessage from './cdiEncounterMessage'
import If from '../../common/if'
import { EncounterNoteBody } from '../../../../types'
import { ObjectValues } from '../../../types/types'
import { RootState } from '../../../store'

const SafeSegment = Segment as unknown as ComponentType<any>
const SafeGrid = Grid as unknown as ComponentType<any>
const SafeHeader = Header as unknown as ComponentType<any>

const mapStateToProps = (state: RootState) => ({
  currentUser: state.user
})

type ReduxStateProps = ReturnType<typeof mapStateToProps>

type Props = RouteComponentProps & ReduxStateProps & {
  encounterId: string
  onEncounterNav: (id: string) => void
}

/**
 * Provides the top-level CDI encounter detail component
 *
 * @param encounterId Encounter ID
 * @param onEncounterNav Callback for encounter navigation request
 *        Arguments: Encounter ID
 */
function CdiEncounter(
  props: Props
): JSX.Element | null {
  const {
    currentUser,
    encounterId,
    history,
    onEncounterNav
  } = props

  const [
    encounterDetail,
    setEncounterDetail
  ] = useState<EncounterDetail | null>(null)

  const [
    encounterIdState,
    setEncounterIdState
  ] = useState<string>(encounterId)

  const [
    activeConditionId,
    setActiveConditionId
  ] = useState<string | null>(null)


  const [
    activeHccGroupId,
    setActiveHccGroupId
  ] = useState<string | null>(null)


  const [
    isLockFailureModalOpen,
    setIsLockFailureModalOpen
  ] = useState<boolean>(false)

  const [
    isACopiedEncounter,
    setIsACopiedEncounter
  ] = useState<boolean>(false)

  const fetchEncounter = useCallback((
    id: string
  ): void => {
    const url = `${API_URL.GET_ENCOUNTER_DETAIL_BY_ID_WITH_ROLE(currentUser.type)}${id}`
    api.get(url).then((response: AxiosResponse<EncounterDetail>) => {
      const { data } = response
      setEncounterIdState(id)
      setEncounterDetail(data)
    })
  }, [ currentUser.type ])

  useEffect(() => {
    fetchEncounter(encounterIdState)
  }, [ fetchEncounter, encounterIdState ])

  const createOrUpdateConditionRules = (
    condition: EncounterConditionDisplay,
    rules: EncounterConditionRuleRow[],
    callback: (rulesResult: EncounterConditionRuleRow[]) => void
  ): void => {
    const url = API_URL.UPDATE_CONDITION_RULE
    const conditionRules = rules.map(rule => ({
      ...rule,
      encounterConditionId: condition.id
    }))
    if (rules.length > 0) {
      api.put(url, conditionRules).then((response: AxiosResponse<EncounterConditionRuleRow[]>) => {
        if (callback) callback(response.data)
      })
    } else if (callback) callback([])
  }

  const updateConditionWithRules = (
    condition: EncounterConditionDisplay
  ): void => {
    const rules = condition.rules
      ? condition.rules
      : []
    createOrUpdateConditionRules(
      condition,
      rules,
      (rulesResult: any) => {
        const content = (condition.content == null) ? null : condition.content.trim().replace(/ {2,}/, ' ')
        const url = `${API_URL.UPDATE_CONDITION}/${condition.id}`
        const conditionRevised = {
          ...condition,
          content: content
        }
        api.put(url, conditionRevised).then((response: AxiosResponse<EncounterConditionDisplay>) => {
          const data = {
            ...response.data,
            rules: rulesResult
          }
          if (
            encounterDetail
            && encounterDetail.conditions
            && encounterDetail.encounter
          ) {
            const encounterDetailRevised = {
              ...encounterDetail,
              conditions: withEncounter(
                spliceCondition(
                  data,
                  encounterDetail.conditions
                ),
                encounterDetail.encounter
              )
            } as EncounterDetail
            setActiveConditionId(conditionRevised.id)
            setEncounterDetail(encounterDetailRevised)
          }
        })
      }
    )
  }

  const onEncounterNavAction = (id: string | undefined): void => {
    if (id) {
      onEncounterNav(id)
    }
  }

  const updateCdiTags = (
    condition: EncounterConditionDisplay
  ): void => {
    const url = API_URL.UPDATE_CONDITION_CDI_TAGS(condition.id)
    /**
     * We should use the response to splice back in the CDI tags and have that flow downnstream
     * to the consuming components. ~Tal
     */
    api.put(url, condition.cdiTags).then(() => {
      setActiveConditionId(condition.id)
      if (
        encounterDetail
        && encounterDetail.conditions
        && encounterDetail.encounter
      ) {
        setEncounterDetail({
          ...encounterDetail,
          conditions: withEncounter(
            spliceCondition(
              condition,
              encounterDetail.conditions
            ),
            encounterDetail.encounter
          )
        })
      }
    })
  }

  const createCondition = (
    condition: EncounterConditionDisplay
  ): void => {
    const rules = condition.rules
      ? condition.rules
      : []
    const id = (
      encounterDetail
      && encounterDetail.encounter
      && encounterDetail.encounter.id
    )
      ? encounterDetail.encounter.id
      : ''
    const conditionRevised = {
      ...condition,
      encounter: { id },
      content: (
        condition
        && condition.content
        && condition.content.trim().replace(/ {2,}/, ' ')
      )
    }
    const url = API_URL.CREATE_CONDITION
    api.post(url, conditionRevised).then(
      (response: AxiosResponse<EncounterConditionDisplay>) => {
        createOrUpdateConditionRules(
          response.data,
          rules,
          rulesResult => {
            response.data.rules = rulesResult
            if (
              encounterDetail
              && encounterDetail.conditions
              && encounterDetail.encounter
            ) {
              encounterDetail.conditions.push(response.data)
              encounterDetail.conditions = withEncounter(
                encounterDetail.conditions,
                encounterDetail.encounter
              )
            }
            setActiveConditionId(response.data.id)
            setEncounterDetail(encounterDetail)
          }
        )
      }
    )
  }

  const toggleLock = () => {
    if (
      encounterDetail
      && encounterDetail.encounter
      && encounterDetail.encounter.status
    ) {
      const encounterStatus = encounterDetail.encounter.status
      const isIgnored = encounterStatus === ENCOUNTER.STATUS.IGNORED
      const onSuccess = () => {
        if (isIgnored) {
          history.push(PAGES.HOME)
        } else {
          setEncounterDetail(null)
          fetchEncounter(encounterIdState)
        }
      }
      const onFailure = () => {
        setIsLockFailureModalOpen(true)
      }
      if (
        encounterDetail
        && encounterDetail.encounter
      ) {
        lockUtils.toggleLock(
          encounterDetail.encounter.lock,
          encounterIdState,
          currentUser,
          onSuccess,
          onFailure
        )
      }
    }
  }

  const updateReviewQueryStatus = (
    queryStatus: string
  ) => {
    const encounterID = (
      encounterDetail
      && encounterDetail.encounter
      && encounterDetail.encounter.id
    )
      ? encounterDetail.encounter.id
      : ''
    const reviewID = (
      encounterDetail
      && encounterDetail.encounter
      && encounterDetail.encounter.review
    )
      ? encounterDetail.encounter.review.id
      : ''
    const url = `${API_URL.UPDATE_REVIEW_QUERY_STATUS}/${reviewID}/${queryStatus}`
    api.put(url).then(({ data }) => {
      if (data.queryStatusCode === REVIEW.QUERY_STATUS.REVIEW_QUERY_OUTSTANDING) {
        lockUtils.unlockEncounter(
          encounterID,
          () => {
            history.push(PAGES.HOME)
          }
        )
      } else {
        const encounterDetailEncounter = (
          encounterDetail
          && encounterDetail.encounter
        )
          ? encounterDetail.encounter
          : {}
        setEncounterDetail({
          ...encounterDetail,
          encounter: {
            ...encounterDetailEncounter,
            review: data
          }
        })
      }
    })
  }

  const updateReviewCode = (
    reviewCode: ObjectValues<typeof REVIEW.CODE>
  ): void => {
    if (
      encounterDetail
      && encounterDetail.encounter
    ) {
      const reviewID = (
        encounterDetail.encounter.review &&
        encounterDetail.encounter.review.id
      )
      const url = `${API_URL.UPDATE_REVIEW_CODE}/${reviewID}/${reviewCode}`
      api.put(url, {}).then(() => {
        const destination = allowPatientTabSchedulingCdi(currentUser)
          ? PAGES.PATIENT_LIST
          : PAGES.HOME
        lockUtils.unlockEncounter(
          encounterIdState,
          () => {
            history.push(destination)
          }
        )
      })
    }
  }

  const onHeaderAction = (
    action: keyof typeof ENCOUNTER.ACTION
  ): void => {
    if (action === ENCOUNTER.ACTION.LOCK_TOGGLE) {
      toggleLock()
    }
    if (action === ENCOUNTER.ACTION.REVIEW_QUERY_OUTSTANDING) {
      updateReviewQueryStatus(REVIEW.QUERY_STATUS.REVIEW_QUERY_OUTSTANDING)
    }
    if (action === ENCOUNTER.ACTION.REVIEW_QUERY_RESOLVED) {
      updateReviewQueryStatus(REVIEW.QUERY_STATUS.REVIEW_QUERY_RESOLVED)
    }
    if (action === ENCOUNTER.ACTION.MARK_OPEN) {
      updateReviewCode(REVIEW.CODE.OPEN)
    }
    if (action === ENCOUNTER.ACTION.MARK_READY_FOR_CDI_QA) {
      updateReviewCode(REVIEW.CODE.READY_FOR_CDI_QA)
    }
    if (action === ENCOUNTER.ACTION.MARK_READY_FOR_PHYSICIAN) {
      updateReviewCode(REVIEW.CODE.READY_FOR_PHYSICIAN)
    }
  }

  const updateEncounterStatus = (
    encounterStatus: string
  ): void => {
    if (
      encounterDetail
      && encounterDetail.encounter
      && encounterDetail.encounter.id
    ) {
      const baseUrl = API_URL.UPDATE_ENCOUNTER_STATUS_WITH_ROLE(currentUser.type)
      const url = `${baseUrl}/${encounterDetail.encounter.id}/${encounterStatus}`
      api.put(url, {}).then(() => {
        const encounterDetailRevised = {
          ...encounterDetail,
          encounter: {
            ...encounterDetail.encounter,
            status: encounterStatus
          }
        } as EncounterDetail
        setEncounterDetail(encounterDetailRevised)
      })
    }
  }

  const onLeftAction = (action: keyof typeof ENCOUNTER.ACTION): void => {
    if (action === ENCOUNTER.ACTION.STATUS_TOGGLE_PLANNED) {
      updateEncounterStatus(ENCOUNTER.STATUS.PLANNED)
    }
    if (action === ENCOUNTER.ACTION.STATUS_TOGGLE_CANCELLED) {
      updateEncounterStatus(ENCOUNTER.STATUS.CANCELLED)
    }
    if (action === ENCOUNTER.ACTION.STATUS_TOGGLE_IGNORED) {
      updateEncounterStatus(ENCOUNTER.STATUS.IGNORED)
    }
  }

  /**
   * Currently CDI condition groups are modal: either 1 condition (or condition
   * group) is active or no conditions are active
   */
  const onHccGroupDeactivate = (): void => {
    setActiveHccGroupId(null)
  }

  /**
   * @param hccGroupId Null or HCC group identifier
   */
  const onHccGroupActivate = (hccGroupId: string): void => {
    setActiveConditionId(null)
    setActiveHccGroupId(hccGroupId)
  }

  /**
   * @param condition Can be: null, condition or ID. An ID can be a condition or
   * a group identifier
   */
  const onConditionActivate = (
    condition: EncounterConditionDisplay | string | null
  ): void => {
    if (condition === null) {
      setActiveConditionId(null)
    } else {
      const id = typeof (condition) === 'string'
        ? condition
        : condition.id
      setActiveConditionId(id)
      setActiveHccGroupId(null)
    }
  }

  const onConditionDeactivate = (): void => {
    setActiveConditionId(null)
  }

  const onHccGroupAction = (
    action: keyof typeof HCC_GROUP.ACTION,
    hccGroupId: string
  ): void => {
    if (action === HCC_GROUP.ACTION.ACTIVATE) {
      onHccGroupActivate(hccGroupId)
    }
    if (action === HCC_GROUP.ACTION.DEACTIVATE) {
      onHccGroupDeactivate()
    }
  }

  const updateConditions = (
    conditions: EncounterConditionDisplay[],
    newCode: string
  ): void => {
    const url = API_URL.UPDATE_CONDITION

    const conditionsRevised = conditions.map(
      (condition: EncounterConditionDisplay) => ({
        ...condition,
        conditionCode: newCode
      })
    )

    api.put(url, conditionsRevised).then(
      (response: AxiosResponse<EncounterConditionDisplay[]>) => {
        const encounterDetailRevised = {
          ...encounterDetail,
          conditions: spliceConditions(
            response.data,
            encounterDetail && encounterDetail.conditions
          )
        }
        setEncounterDetail(encounterDetailRevised as EncounterDetail)
      }
    )
  }

  const updateCondition = (condition: EncounterConditionDisplay) => {
    // Remove whitespace from content
    if (condition.content) {
      condition = {
        ...condition,
        content: condition.content.trim().replace(/ {2,}/, ' ')
      }
    }

    const url = `${API_URL.UPDATE_CONDITION}/${condition.id}`
    api.put(
        url,
        condition
      ).then((response: AxiosResponse<EncounterConditionDisplay>) => {
        if (
          encounterDetail
          && encounterDetail.conditions
          && encounterDetail.encounter
        ) {
          const encounterDetailRevised = {
            ...encounterDetail,
            conditions: withEncounter(
              spliceCondition(
                response.data,
                encounterDetail.conditions
              ),
              encounterDetail.encounter
            )
          } as EncounterDetail
          setActiveConditionId(condition.id)
          setEncounterDetail(encounterDetailRevised)
        }
      })
  }

  const onConditionAction = (
    action: keyof typeof CONDITION.ACTION,
    conditionOrConditions: (
      EncounterConditionDisplay[] |
      EncounterConditionDisplay |
      string |
      Partial<EncounterConditionDisplay>
    )
  ) => {
    if (action === CONDITION.ACTION.ACTIVATE) {
      onConditionActivate(
        conditionOrConditions as EncounterConditionDisplay
      )
    }
    if (action === CONDITION.ACTION.DEACTIVATE) {
      onConditionDeactivate()
    }
    if (conditionOrConditions) {
      if (action === CONDITION.ACTION.MARK_ACTIVITY) {
        updateCondition({
          ...conditionOrConditions as EncounterConditionDisplay,
          conditionStatus: CONDITION.TYPE.ACTIVITY
        })
      }
      if (action === CONDITION.ACTION.MARK_KNOWN) {
        updateCondition({
          ...conditionOrConditions as EncounterConditionDisplay,
          conditionStatus: CONDITION.TYPE.KNOWN
        })
      }
      if (action === CONDITION.ACTION.MARK_OPEN) {
        updateCondition({
          ...conditionOrConditions as EncounterConditionDisplay,
          conditionCode: CONDITION.CODE.OPEN
        })
      }
      if (action === CONDITION.ACTION.MARK_POTENTIAL) {
        updateCondition({
          ...conditionOrConditions as EncounterConditionDisplay,
          conditionStatus: CONDITION.TYPE.POTENTIAL
        })
      }
      if (action === CONDITION.ACTION.MARK_REMOVED) {
        updateCondition({
          ...conditionOrConditions as EncounterConditionDisplay,
          conditionCode: CONDITION.CODE.REMOVED
        })
      }
      if (action === CONDITION.ACTION.MARK_REMOVED_MULTIPLE) {
        updateConditions(
          conditionOrConditions as EncounterConditionDisplay[],
          CONDITION.CODE.REMOVED
        )
      }
      if (action === CONDITION.ACTION.CREATE) {

        createCondition(
          conditionOrConditions as EncounterConditionDisplay
        )
      }
      if (action === CONDITION.ACTION.UPDATE) {
        updateCondition(
          conditionOrConditions as EncounterConditionDisplay
        )
      }
      if (action === CONDITION.ACTION.UPDATE_WITH_RULES) {
        updateConditionWithRules(
          conditionOrConditions as EncounterConditionDisplay
        )
      }
      if (action === CONDITION.ACTION.UPDATE_CDI_TAGS) {
        updateCdiTags(
          conditionOrConditions as EncounterConditionDisplay
        )
      }
    }
  }

  const onLockFailureModalClose = () => {
    setEncounterDetail(null)
    setIsLockFailureModalOpen(false)
    if (
      encounterDetail
      && encounterDetail.encounter
      && encounterDetail.encounter.id
    ) {
      fetchEncounter(encounterDetail.encounter.id)
    }
  }

  const createConditionNote = (
    condition: EncounterConditionDisplay,
    content: string,
    reason: string
  ) => {
    const url = API_URL.CREATE_ENCOUNTER_NOTE
    const body = {
      encounterId: encounterIdState,
      encounterConditionId: condition.id,
      noteBody: content,
      noteReasonCode: reason || NOTE.REASON.OPEN
    }
    const currentRole = currentUser.type
    if (
      encounterDetail
      && encounterDetail.encounter
      && encounterDetail.encounter.id
      && encounterDetail.conditions
    ) {
      const { id } = encounterDetail.encounter
      api.post(url, body).then(() => {
        api.get(API_URL.GET_ENCOUNTER_CONDITION_NOTES_WITH_ROLE(
          id,
          condition.id,
          currentRole
        )).then(({ data }) => {
          if (
            encounterDetail
            && encounterDetail.conditions
          ) {
            const conditions = encounterDetail.conditions.map(c => {
              if (c.id === condition.id) {
                return {
                  ...condition,
                  notes: data
                }
              }
              return c
            })
            setActiveConditionId(condition.id)
            if (encounterDetail) {
              setEncounterDetail(
                {
                  ...encounterDetail,
                  conditions
                }
              )
            }
          }
        })
      })
    }
  }

  const onConditionNoteAction = (
    action: keyof typeof NOTE.ACTION,
    condition: EncounterConditionDisplay,
    content: string,
    reason: string
  ): void => {
    if (action === NOTE.ACTION.CREATE) {
      createConditionNote(
        condition,
        content,
        reason
      )
    }
  }

  const createEncounterNote = (
    content: string,
    reason: keyof typeof NOTE.REASON
  ): void => {
    if (
      encounterDetail
      && encounterDetail.encounter
      && encounterDetail.encounter.id
    ) {
      const { id } = encounterDetail.encounter
      const url = API_URL.CREATE_ENCOUNTER_NOTE
      const body: EncounterNoteBody = {
        encounterId: id,
        noteBody: content,
        noteReasonCode: reason || NOTE.REASON.OPEN
      }
      api.post(url, body).then(() => {
        api.get(API_URL.GET_ENCOUNTER_NOTES_WITH_ROLE(
          id,
          currentUser.type
        )).then(({ data }) => {
          setEncounterDetail({
            ...encounterDetail,
            notes: data
          })
          setActiveConditionId(activeConditionId)
        })
      })
    }
  }

  const onSubmitEncounterNote = (
    noteID: string,
    noteBody: string
  ): void => {
    createEncounterNote(
      noteBody,
      NOTE.REASON.OPEN
    )
  }

  const setIsACopiedEncounterFn = (bool: boolean): void => {
    setIsACopiedEncounter(bool)
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    })
  }

  const renderDetail = (
    detail: EncounterDetail
  ): JSX.Element | null => {
    const { encounter } = detail
    if (encounter) {
      const { notes } = detail
      const key = (new Date()).getTime()
      const encounterLeftKey = `cdi-encounter-left-${encounter.id}-${encounter.status}`
      const shouldAllowCDICopyPaste = allowEncounterCDICopyPaste(currentUser)
      const displayVersionValue = encounterConditionCdiDisplayVersion(currentUser)
      const displayVersion = displayVersionValue === '1'
        ? '1'
        : '2'
      const containerId = `cdi-encounter-${encounter.id}`
      return (
        <div
          id={ containerId }
          className="header-padding"
        >
          <Container>
            <CdiEncounterHeader
              key={ `header-${key}` }
              encounter={ encounter }
              isACopiedEncounter={ isACopiedEncounter }
              onAction={ onHeaderAction }
            />
            <SafeSegment
              attached
              className="no-margin">
              <SafeGrid stackable>
                <CdiEncounterMessage
                  encounter={ encounter }
                  currentUser={ currentUser }
                  mode="grid"
                />
                <Grid.Row>
                  <Grid.Column
                    width={
                      shouldRenderReskin2025
                        ? 5
                        : 4
                    }
                  >
                    <CdiEncounterLeft
                      key={ encounterLeftKey }
                      encounterDetail={ detail }
                      onAction={ onLeftAction }
                    />
                  </Grid.Column>
                  <Grid.Column
                    width={
                      shouldRenderReskin2025
                        ? 11
                        : 12
                    }
                    className="right-column"
                  >
                    <ConditionHeader
                      // conditions={
                      //   (
                      //     encounterDetail
                      //     && encounterDetail.conditions
                      //   )
                      //     ? encounterDetail.conditions
                      //     : []
                      // }
                      conditions={ [] }
                      title="Notes"
                      displayIndex={ 0 }
                    />
                    <Accordion
                      fluid
                      styled
                    >
                      <Accordion.Content>
                        <EncounterNotes
                          key={ `encounter-notes-${notes
                            ? notes.length
                            : 0}` }
                          encounterId={
                            encounter
                              && encounter.id
                              ? encounter.id
                              : ''
                          }
                          notes={ notes }
                          onSubmit={ onSubmitEncounterNote }
                        />
                      </Accordion.Content>
                    </Accordion>
                    {
                      displayVersion === '1' ? (
                        <CdiEncounterConditionsV1
                          key={ `encounter-conditions-${key}` }
                          activeConditionId={ activeConditionId }
                          encounterDetail={ detail }
                          onConditionAction={ onConditionAction }
                          onConditionNoteAction={ onConditionNoteAction }
                        />
                      ) : (
                        <CdiEncounterConditionsV2
                          key={ `encounter-conditions-${key}` }
                          activeConditionId={ activeConditionId as string }
                          activeHccGroupId={ activeHccGroupId }
                          encounterDetail={ detail }
                          onConditionAction={ onConditionAction }
                          onConditionNoteAction={ onConditionNoteAction }
                          onHccGroupAction={ onHccGroupAction }
                          conditionGroups={ undefined }
                        />
                      )
                    }
                    <div>
                      <ConditionHeader
                        // conditions={
                        //   (
                        //     encounterDetail
                        //     && encounterDetail.conditions
                        //   )
                        //     ? encounterDetail.conditions
                        //     : []
                        // }
                        conditions={ [] }
                        title="Previous Encounters"
                        displayIndex={ 0 }
                      />
                      <Accordion fluid styled>
                        <If isTrue={ shouldAllowCDICopyPaste }>
                          <PreviousEncountersSticky
                            key={ encounter.id }
                            encounter={ encounter }
                            isEncounterLocked={
                              encounter
                              && !!encounter.lock
                              && encounter.lock.statusCode
                              && encounter.lock.statusCode === LOCK.STATUS.ACTIVE
                            }
                            subjectId={
                              encounter
                                && encounter.patient
                                && encounter.patient.id
                                ? encounter.patient.id
                                : ''
                            }
                            onClickGoToPrevious={ onEncounterNavAction }
                            setEncounterDetail={ setEncounterDetail }
                            setIsACopiedEncounter={ setIsACopiedEncounterFn }
                          />
                        </If>
                        <If isTrue={ !shouldAllowCDICopyPaste }>
                          <PreviousEncounters
                            key={ encounter.id }
                            encounterId={ encounter.id as string }
                            subjectId={ encounter
                              && encounter.patient
                              && encounter.patient.id
                              ? encounter.patient.id
                              : '' }
                            onClickGoToPrevious={ onEncounterNavAction }
                            // TODO: SHAWN WHAT IS THIS !!!!!!!!!!!!!!!!!!!!!!!!!!!!! WAS NOT BEING PASSED IN
                            encounter={ undefined }
                            // TODO: SHAWN WHAT IS THIS !!!!!!!!!!!!!!!!!!!!!!!!!!!!! WAS NOT BEING PASSED IN
                            index={ 0 }
                          />
                        </If>
                      </Accordion>
                    </div>
                  </Grid.Column>
                </Grid.Row>
              </SafeGrid>
            </SafeSegment>
          </Container>
          <Modal
            open={ isLockFailureModalOpen }
            onClose={ onLockFailureModalClose }
            basic size="small">
            <SafeHeader icon="lock" content="Encounter Lock Conflict" />
            <Modal.Content>
              <h3>
                <div>
                  Sorry, could not lock this encounter for you.
                </div>
                <div>
                  Between opening the encounter and clicking the lock icon,
                  another user has acquired a lock on this encounter.
                </div>
              </h3>
            </Modal.Content>
            <Modal.Actions>
              <Button
                id="cdi-encounter-lock-failure-modal-close"
                color="green"
                onClick={ onLockFailureModalClose }
                inverted
              >
                <Icon name="checkmark" /> Ok
              </Button>
            </Modal.Actions>
          </Modal>
        </div>
      )
    }
    return null
  }

  return encounterDetail
    ? renderDetail(encounterDetail)
    : null
}

export default withRouter(connect(mapStateToProps)(CdiEncounter))
